/**
 * Payment Success Page
 *
 * Displayed after successful Stripe checkout. FIX-6: now verifies the
 * session_id against the BE before rendering success. Previously this
 * page rendered "Payment Successful!" for ANY ?session_id=* in the URL
 * with no BE round-trip — trivially spoofable.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { ROUTES } from '../../constants/routes';

type VerifyState =
  | { kind: 'loading' }
  | { kind: 'verified'; planName: string | null; amount: number | null; currency: string | null }
  | { kind: 'failed'; reason: 'client_error' | 'server_error'; message: string };

const PaymentSuccessPage = () => {
  const { t } = useTranslation('pricing');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUserData, session } = useAuth();
  const [state, setState] = useState<VerifyState>({ kind: 'loading' });

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    let cancelled = false;

    const verify = async () => {
      // Without an access token we can't call the BE verify endpoint.
      // Route the user back to /pricing rather than rendering an
      // unverified success page.
      if (!session?.access_token) {
        if (!cancelled) {
          setState({
            kind: 'failed',
            reason: 'client_error',
            message: t(
              'payment.verifyFailed.unauthenticated',
              'You must be signed in to verify a payment.'
            ),
          });
        }
        return;
      }

      try {
        const resp = await fetch(
          `${import.meta.env.VITE_API_URL}/api/stripe/verify-session`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ session_id: sessionId }),
          }
        );

        if (cancelled) return;

        if (resp.ok) {
          const body = (await resp.json()) as {
            verified?: boolean;
            plan_name?: string | null;
            amount?: number | null;
            currency?: string | null;
          };
          // Force-refresh subscription state so /pricing, nav badges, etc.
          // show the new plan instead of stale "not subscribed" data.
          try {
            await refreshUserData();
          } catch {
            // refreshUserData failures are non-fatal here — the BE already
            // confirmed the payment via Stripe. The webhook will update
            // user_subscriptions on its own schedule.
          }
          if (cancelled) return;
          setState({
            kind: 'verified',
            planName: body.plan_name ?? null,
            amount: body.amount ?? null,
            currency: body.currency ?? null,
          });
          return;
        }

        // 4xx = client error (spoofed session, wrong user, unpaid). Direct
        // the user back to /pricing instead of celebrating.
        // 5xx = transient — webhook may still complete; tell them to wait.
        const reason: 'client_error' | 'server_error' =
          resp.status >= 500 ? 'server_error' : 'client_error';
        const fallbackMessage =
          reason === 'server_error'
            ? t(
                'payment.verifyFailed.server',
                'Unable to verify payment right now. Your subscription will activate shortly. Contact support if it does not.'
              )
            : t(
                'payment.verifyFailed.client',
                'Payment verification failed. Please return to pricing and try again.'
              );
        setState({ kind: 'failed', reason, message: fallbackMessage });
      } catch (err) {
        if (cancelled) return;
        // Network error → treat as transient server-side.
        console.error('verify-session failed:', err);
        setState({
          kind: 'failed',
          reason: 'server_error',
          message: t(
            'payment.verifyFailed.server',
            'Unable to verify payment right now. Your subscription will activate shortly. Contact support if it does not.'
          ),
        });
      }
    };

    verify();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, refreshUserData, session, t]);

  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">
            {t('payment.verifying', 'Verifying your payment...')}
          </p>
        </div>
      </div>
    );
  }

  if (state.kind === 'failed') {
    const isServerError = state.reason === 'server_error';
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-2xl shadow-xl p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-6">
              <svg className="h-10 w-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-4">
              {isServerError
                ? t('payment.verifyFailed.serverTitle', 'Verification pending')
                : t('payment.verifyFailed.clientTitle', 'Payment verification failed')}
            </h2>
            <p className="text-base text-muted-foreground mb-8">{state.message}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/pricing')}
                className="w-full bg-primary text-primary-foreground py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                {t('payment.verifyFailed.backToPricing', 'Back to pricing')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // state.kind === 'verified'
  const formattedAmount =
    state.amount != null && state.currency
      ? new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: state.currency.toUpperCase(),
        }).format(state.amount)
      : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-card rounded-2xl shadow-xl p-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t('payment.success.title', 'Payment Successful!')}
          </h2>

          {/* Plan + amount line — only renders if BE returned them */}
          {(state.planName || formattedAmount) && (
            <p className="text-base text-foreground mb-4">
              {state.planName && (
                <span className="font-semibold">{state.planName}</span>
              )}
              {state.planName && formattedAmount && ' · '}
              {formattedAmount}
            </p>
          )}

          {/* Message */}
          <p className="text-lg text-muted-foreground mb-8">
            {t('payment.success.message', 'Your subscription has been activated. You now have access to all premium features.')}
          </p>

          {/* Features List */}
          <div className="bg-muted rounded-lg p-6 mb-8 text-left">
            <h3 className="font-semibold text-foreground mb-4">
              {t('payment.success.featuresTitle', "What's next?")}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-foreground">{t('payment.success.feature1', 'Start processing your data')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-foreground">{t('payment.success.feature2', 'Train custom models')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-foreground">{t('payment.success.feature3', 'Access priority support')}</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={() => navigate(ROUTES.STANDARDISIERUNG)}
              className="w-full bg-primary text-primary-foreground py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              {t('payment.success.startButton', 'Start Using Your Plan')}
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-muted text-foreground py-3 px-6 rounded-lg font-semibold hover:bg-muted/80 transition-colors"
            >
              {t('payment.success.viewPlanButton', 'View My Plan')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
